import type { LedOutput } from '../../services/admin';
import { ledOutputLabel, sortLedOutputs, type LedMappingRow } from './ledOutputs';

// Model editor "LEDs" section: which LED outputs from the global catalog this model supports,
// and this model's OEM Android intent for each. A model may map any subset - or none.
export default function LedMappingsEditor({
  rows,
  outputs,
  onChange,
}: {
  rows: LedMappingRow[];
  outputs: LedOutput[];
  onChange: (rows: LedMappingRow[]) => void;
}) {
  const sorted = sortLedOutputs(outputs);
  const update = (index: number, patch: Partial<LedMappingRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <div className="border-t border-gray-700 pt-4 space-y-3" data-led-mappings>
      <p className="text-sm font-semibold text-gray-300">LEDs</p>
      <p className="text-xs text-gray-500 -mt-2">
        Pick the LED outputs this model supports from the LED Outputs catalog, then enter the Android intent this model's
        hardware uses for each - intents vary by OEM/model, so they're defined per model.
      </p>
      <div className="space-y-2">
        {rows.map((row, index) => {
          const usedElsewhere = new Set(rows.filter((_, i) => i !== index).map((r) => r.led_output_id).filter(Boolean));
          const known = !row.led_output_id || sorted.some((o) => o.id === row.led_output_id);
          return (
            <div key={index} className="flex gap-2 items-start" data-led-row>
              <select
                value={row.led_output_id}
                onChange={(e) => update(index, { led_output_id: e.target.value })}
                className="w-40 shrink-0 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                aria-label={`LED ${index + 1} output`}
              >
                <option value="">Select LED…</option>
                {sorted.map((o) => (
                  <option key={o.id} value={o.id} disabled={usedElsewhere.has(o.id)}>
                    {ledOutputLabel(o)}
                  </option>
                ))}
                {!known && <option value={row.led_output_id}>(unknown output)</option>}
              </select>
              <input
                type="text"
                value={row.intent_action}
                onChange={(e) => update(index, { intent_action: e.target.value })}
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent font-mono text-xs"
                placeholder="com.intent.redled.on"
                aria-label={`LED ${index + 1} intent`}
              />
              <button
                type="button"
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
                className="px-3 py-2 text-red-400 hover:text-red-300 font-semibold text-sm"
              >
                Remove
              </button>
            </div>
          );
        })}
        {rows.length === 0 && <p className="text-xs text-gray-500 italic">No LEDs mapped for this model.</p>}
        {outputs.length === 0 && (
          <p className="text-xs text-yellow-400">The LED Outputs catalog is empty - add outputs there first.</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange([...rows, { led_output_id: '', intent_action: '' }])}
        className="text-sm text-blue-400 hover:text-blue-300 font-semibold"
      >
        + Add LED
      </button>
    </div>
  );
}
